import type { ApiEffect, Page, PageableInfo, PageInfo } from '../index.h'
import type { MediaUrl } from '../interfaces/generic'
import type {
  CategoryWithShows,
  CategoryWithShowsApi,
  CurrentTvShow,
  GetAllTvShowsParams,
  GetPurchasedTvShowsParams,
  IGenerateSeriesPlaylistParams,
  SeriesPlaylist,
  SeriesPlaylistRecalculate,
  TVShowsPlaylist,
  TvShow,
  TvShowCategory,
  TvShowEpisode,
  TvShowRecommended,
  TvShowSeason,
  TvShowUpdated,
  TvShowUpdateObj,
} from '../interfaces/tvshows'

import {
  attach,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { produce } from 'immer'

const DEFAULT_PAGE_SIZE = 36

//*
//* EFFECTS
//*

// GET /v3/tvshows?sort-by=updatedTime&sort-order=desc
export const getLastAddedTvShowsFx: ApiEffect<
  {
    page: number
    count?: number
    categoryId?: number
    collectionId?: string
    onlyFavorites?: boolean
  },
  Page<TvShow>
> = createEffect()

// GET /v3/tvshows
export const getAllTvShowsFx: ApiEffect<
  GetAllTvShowsParams,
  Page<TvShow>
> = createEffect()

// GET /v3/tvshows?only-favorites=true
export const getOnlyFavoritesTvShowsRowFx = attach({
  effect: getAllTvShowsFx,
  mapParams: () => ({ page: 0, onlyFavorites: true }),
})

export const getOnlyFavoritesTvShowsGridFx = attach({
  effect: getAllTvShowsFx,
  mapParams: (params?: GetAllTvShowsParams) => ({
    ...params,
    page: params?.page || 0,
    onlyFavorites: true,
  }),
})

// GET /v3/library/tvshows
export const getPurchasedTvShowsFx: ApiEffect<
  GetPurchasedTvShowsParams,
  Page<TvShow>
> = createEffect()

export const getPurchasedTvShowsRowFx = attach({
  effect: getPurchasedTvShowsFx,
  mapParams: (params?: GetPurchasedTvShowsParams) => ({
    ...params,
    page: params?.page || 0,
  }),
})

export const getPurchasedTvShowsGridFx = attach({
  effect: getPurchasedTvShowsFx,
  mapParams: (params?: GetPurchasedTvShowsParams) => ({
    ...params,
    page: params?.page || 0,
  }),
})

// GET /v3/tvshows?ids=tvShowId
export const getTvShowBaseFx: ApiEffect<{ tvShowId: number }, TvShow[]> =
  createEffect()
export const getTvShowFx = attach({ effect: getTvShowBaseFx })

// GET /v3/tvshows?categoryId=categoryId
export const getShowsByCategoryIdFx: ApiEffect<
  {
    page: number
    count?: number
    categoryId: number
  },
  Page<TvShow>
> = createEffect()

// GET /v3/tvshows?updated-only=true
export const getTvShowsUpdatedOnlyFx: ApiEffect<void, TvShowUpdated[]> =
  createEffect()

// GET /v3/tvshows/categories
export const getTvShowCategoriesFx: ApiEffect<
  { page: number; count?: number; sortOrder?: string },
  Page<TvShowCategory>
> = createEffect()

// GET /v3/tvshows/categories/{categoryId}/subcategories
export const getTvShowSubcategoriesByCategoryFx: ApiEffect<
  { page: number; categoryId: number; count?: number; sortOrder?: string },
  Page<TvShowCategory>
> = createEffect()

// GET /v3/tvshows/{tvShowId}/trailer/url
export const getTvShowTrailerUrlFx: ApiEffect<{ tvShowId: number }, MediaUrl> =
  createEffect()

// PATCH /v3/tvshows/{tvShowId}
export const updateTvShowFx: ApiEffect<TvShowUpdateObj, void> = createEffect()

// PUT /v3/tvshows/{tvShowId}/season/{seasonId}/episodes/{episodeId}/continue-watching
export const updateContinueWatchingTvShowFx: ApiEffect<
  {
    tvShowId: number
    seasonId: number
    episodeId: number
    stoppedTime: number
    lengthMillis: number
  },
  void
> = createEffect()

// DELETE /v3/tvshows/{tvShowId}/season/{seasonId}/episodes/{episodeId}/continue-watching
export const removeContinueWatchingTvShowFx: ApiEffect<
  {
    tvShowId: number
    seasonId: number
    episodeId: number
  },
  void
> = createEffect()

// PUT /v3/tvshows/{tvShowId}/episodes/{episodeId}/continue-watching
export const updateContinueWatchingWithoutSeasonFx: ApiEffect<
  {
    tvShowId: number
    episodeId: number
    stoppedTime: number
    lengthMillis: number
  },
  void
> = createEffect()

// DELETE /v3/tvshows/{tvShowId}/episodes/{episodeId}/continue-watching
export const removeContinueWatchingWithoutSeasonFx: ApiEffect<
  { tvShowId: number; episodeId: number },
  void
> = createEffect()

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}
export const getTvShowEpisodeBaseFx: ApiEffect<
  { tvShowId: number; episodeId: number; seasonId: number },
  TvShowEpisode
> = createEffect()

export const getTvShowEpisodeFx = attach({
  effect: getTvShowEpisodeBaseFx,
})

// GET /v3/tvshows/{tvShowId}/episodes/{episodeId}
export const getTvShowEpisodeWithoutSeasonBaseFx: ApiEffect<
  { tvShowId: number; episodeId: number },
  TvShowEpisode
> = createEffect()

export const getTvShowEpisodeWithoutSeasonFx = attach({
  effect: getTvShowEpisodeWithoutSeasonBaseFx,
})

// GET /v3/tvshows/{tvShowId}/season/{seasonId}/episodes
export const getTvShowEpisodesBySeasonByPageBaseFx: ApiEffect<
  {
    tvShowId: number
    seasonId: number
    page: number
    count?: number
    sortBy?: string
    sortOrder?: string
  },
  Page<TvShowEpisode>
> = createEffect()

export const getTvShowEpisodesBySeasonByPageFx = attach({
  effect: getTvShowEpisodesBySeasonByPageBaseFx,
})
// GET /v3/tvshows/{tvShowId}/episodes
export const getTvShowEpisodesWithoutSeasonByPageBaseFx: ApiEffect<
  {
    tvShowId: number
    page: number
    count?: number
    sortBy?: string
    sortOrder?: string
  },
  Page<TvShowEpisode>
> = createEffect()
export const getTvShowEpisodesWithoutSeasonByPageFx = attach({
  effect: getTvShowEpisodesWithoutSeasonByPageBaseFx,
})
// GET /v3/tvshows/{tvShowId}/seasons
export const getTvShowSeasonsByPageBaseFx: ApiEffect<
  {
    tvShowId: number
    page: number
    count?: number
    sortBy?: string
    sortOrder?: string
  },
  Page<TvShowSeason>
> = createEffect()

export const getTvShowSeasonsByPageFx = attach({
  effect: getTvShowSeasonsByPageBaseFx,
})

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}
export const getTvShowSeasonByIdBaseFx: ApiEffect<
  {
    tvShowId: number
    seasonId: number
  },
  TvShowSeason
> = createEffect()

export const getTvShowSeasonByIdFx = attach({
  effect: getTvShowSeasonByIdBaseFx,
})

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/url
export const getTvShowEpisodeUrlFx: ApiEffect<
  { tvShowId: number; seasonId: number; episodeId: number },
  MediaUrl
> = createEffect()

// GET /v3/tvshows/{tvShowId}/episodes/{episodeId}/url
export const getEpisodeUrlWithoutSeasonFx: ApiEffect<
  { tvShowId: number; episodeId: number },
  MediaUrl
> = createEffect()

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}
// GET /v3/tvshows/{tvShowId}/episodes/{episodeId}
export const getTvShowEpisodesSmartFx: ApiEffect<
  {
    tvShowId: number
    page: number
    seasonId?: number
    count?: number
    sortBy?: string
    sortOrder?: string
  },
  Page<TvShowEpisode>
> = createEffect()

export const getAllSeasonsFx: ApiEffect<
  { tvShowId: number },
  Array<TvShowSeason>
> = createEffect()

export const getAllSeasonsByShowIdFx = attach({
  effect: getAllSeasonsFx,
})

export const getSeasonsWithEpisodesFx: ApiEffect<
  { tvShowId: number },
  TVShowsPlaylist
> = createEffect()

export const getAllEpisodesFx: ApiEffect<
  { tvShowId: number; seasonId?: number },
  Array<TvShowEpisode>
> = createEffect()

export const getAllEpisodesBySeasonFx = attach({
  effect: getAllEpisodesFx,
})

export const getCurrenShowByIdFx = attach({
  effect: getTvShowFx,
})

export const getAllEpisodesForSeasonsFx: ApiEffect<
  { seasons: Array<{ id: number }>; tvShowId: number },
  { [key: number]: Array<TvShowEpisode> }
> = createEffect()

// POST /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/watched
export const postMarkEpisodeWatchedFx: ApiEffect<
  { tvShowId: number; seasonId: number; episodeId: number; watched: boolean },
  void
> = createEffect()

// POST /v3/tvshows/{tvShowId}/episodes/{episodeId}/watched
export const postMarkEpisodeWatchedWithoutSeasonFx: ApiEffect<
  { tvShowId: number; episodeId: number; watched: boolean },
  void
> = createEffect()

// CUSTOM EFFECTS

export const getRecommendedTvShowsFx: ApiEffect<
  {
    categoryId: number
    collectionId?: string
    onlyFavorites?: boolean
    tvShowId: number
    showIndex?: number
  },
  TvShowRecommended[]
> = createEffect()

// GET /v3/tvshows?recommended-to={tvShowId}
export const getPlayerRecommendedTvShowsFx: ApiEffect<
  {
    tvShowId: number
    count?: number
  },
  TvShow[]
> = createEffect()

// imitate GET /categories/{categoryId}
export const getTvShowCategoryFx: ApiEffect<
  { categoryId: number },
  TvShowCategory | null
> = createEffect()

export const getCategoriesWithShowsFx: ApiEffect<
  {
    page: number
    count?: number
  },
  Page<CategoryWithShowsApi>
> = createEffect()

// *
// * HANDLERS
// *

const updateSingleHandler = (
  item: TvShow | null,
  params: TvShowUpdateObj
): TvShow | null => {
  if (!item) return null
  if ('favorite' in params) {
    ;(item as any).__favorite = item.favorite // hack to preserve previous value
    item.favorite = params.favorite
  }
  return { ...item }
}
const updateSingleSuccessHandler = (
  item: TvShow | null,
  params: TvShowUpdateObj
) => {
  if (!item) return null
  if ('favorite' in params && '__favorite' in item) {
    delete (item as any).__favorite
  }
  return { ...item }
}
const updateSingleFailHandler = (
  item: TvShow | null,
  params: TvShowUpdateObj
) => {
  if (!item) return null
  if ('favorite' in params) {
    item.favorite =
      '__favorite' in item ? (item as any).__favorite : !params.favorite
  }
  return { ...item }
}

const onTvShowFavoriteHandler = (
  tvShows: TvShow[],
  params: TvShowUpdateObj
) => {
  const index = tvShows.findIndex((show) => show.id === params.id)
  if (index !== -1) {
    const mutableShow =
      updateSingleHandler(tvShows[index], params) || tvShows[index]
    tvShows = tvShows.slice()
    tvShows.splice(index, 1, mutableShow)
  }
  return tvShows
}

const onTvShowFavoriteDoneHandler = (
  tvShows: TvShow[],
  { params }: { params: TvShowUpdateObj }
) => {
  const index = tvShows.findIndex((show) => show.id === params.id)
  if (index !== -1) {
    const mutableShow =
      updateSingleSuccessHandler(tvShows[index], params) || tvShows[index]
    tvShows = tvShows.slice()
    tvShows.splice(index, 1, mutableShow)
  }
  return tvShows
}

const onTvShowFavoriteFailHandler = (
  tvShows: TvShow[],
  { params }: { params: TvShowUpdateObj }
) => {
  const index = tvShows.findIndex((show) => show.id === params.id)
  if (index !== -1) {
    const mutableShow =
      updateSingleFailHandler(tvShows[index], params) || tvShows[index]
    tvShows = tvShows.slice()
    tvShows.splice(index, 1, mutableShow)
  }
  return tvShows
}

type EpisodeWatchedParams = {
  episodeId: number
  watched: boolean
}

const updateEpisodeWatchedHandler = <T extends TvShowEpisode | null>(
  episode: T,
  params: EpisodeWatchedParams
): T => {
  if (!episode || episode.id !== params.episodeId) return episode

  ;(episode as any).__watched = episode.watched
  ;(episode as any).__stoppedTime = episode.stoppedTime
  episode.watched = params.watched
  episode.stoppedTime = params.watched ? 0 : undefined

  return { ...episode }
}

const updateEpisodeWatchedSuccessHandler = <T extends TvShowEpisode | null>(
  episode: T,
  params: EpisodeWatchedParams
): T => {
  if (!episode || episode.id !== params.episodeId) return episode

  if ('__watched' in episode) delete (episode as any).__watched
  if ('__stoppedTime' in episode) delete (episode as any).__stoppedTime

  return { ...episode }
}

const updateEpisodeWatchedFailHandler = <T extends TvShowEpisode | null>(
  episode: T,
  params: EpisodeWatchedParams
): T => {
  if (!episode || episode.id !== params.episodeId) return episode

  episode.watched =
    '__watched' in episode ? (episode as any).__watched : !params.watched
  episode.stoppedTime =
    '__stoppedTime' in episode ? (episode as any).__stoppedTime : undefined

  return { ...episode }
}

const updateEpisodesWatchedHandler =
  (handler: typeof updateEpisodeWatchedHandler) =>
  (episodes: TvShowEpisode[], params: EpisodeWatchedParams) =>
    episodes.map((episode) => handler(episode, params))

//*
//* STORES
//*
export const $lastAddedTvShowsPageInfo = createStore<PageInfo | null>(null).on(
  getLastAddedTvShowsFx.doneData,
  (_, { content: _ignored, ...pageInfo }) => pageInfo
)
export const $lastAddedTvShows = createStore<TvShow[]>([]).on(
  getLastAddedTvShowsFx.doneData,
  (_, page) => [...page.content]
)

export const $tvShowCategories = createStore<TvShowCategory[]>([]).on(
  getTvShowCategoriesFx.doneData,
  (_, page) => [...page.content]
)

export const $tvShowCategoriesPageInfo = createStore<PageInfo | null>(null).on(
  getTvShowCategoriesFx.doneData,
  (_, { content: _ignored, ...pageInfo }) => pageInfo
)

// GET /v3/tvshows/categories/{categoryId}/subcategories
export const $tvShowSubCategories = createStore<TvShowCategory[]>([]).on(
  getTvShowSubcategoriesByCategoryFx.doneData,
  (_, page) => [...page.content]
)
export const $tvShowSubCategoriesPageInfo = createStore<PageInfo | null>(
  null
).on(
  getTvShowSubcategoriesByCategoryFx.doneData,
  (_, { content: _ignored, ...pageInfo }) => pageInfo
)

// GET /v3/tvshows/{tvShowId}/trailer/url
export const $tvShowTrailerUrl = createStore<MediaUrl | null>(null).on(
  getTvShowTrailerUrlFx.doneData,
  (_, urlObject) => urlObject
)

// GET /v3/tvshows
export const $allTvShows = createStore<TvShow[]>([])
  .on(getAllTvShowsFx.doneData, (_, page) => [...page.content])
  .on(updateTvShowFx, onTvShowFavoriteHandler)
  .on(updateTvShowFx.done, onTvShowFavoriteDoneHandler)
  .on(updateTvShowFx.fail, onTvShowFavoriteFailHandler)

export const $allTvShowsPageInfo = createStore<PageInfo | null>(null).on(
  getAllTvShowsFx.doneData,
  (_, { content: _ignored, ...pageInfo }) => pageInfo
)

// GET /v3/tvshows?&updatedTimeOnly=true
export const $tvShowsUpdatedOnly = createStore<TvShowUpdated[]>([]).on(
  getTvShowsUpdatedOnlyFx.doneData,
  (_, tvShows) => tvShows
)

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}
// GET /v3/tvshows/{tvShowId}/episodes/{episodeId}
export const $tvShowEpisode = createStore<TvShowEpisode | null>(null)
  .on(getTvShowEpisodeFx.doneData, (_, episode) => episode)
  .on(getTvShowEpisodeWithoutSeasonFx.doneData, (_, episode) => episode)
  .on(
    [postMarkEpisodeWatchedFx, postMarkEpisodeWatchedWithoutSeasonFx],
    updateEpisodeWatchedHandler
  )
  .on(
    [
      postMarkEpisodeWatchedFx.done.map(({ params }) => params),
      postMarkEpisodeWatchedWithoutSeasonFx.done.map(({ params }) => params),
    ],
    updateEpisodeWatchedSuccessHandler
  )
  .on(
    [
      postMarkEpisodeWatchedFx.fail.map(({ params }) => params),
      postMarkEpisodeWatchedWithoutSeasonFx.fail.map(({ params }) => params),
    ],
    updateEpisodeWatchedFailHandler
  )

const compareSortOrder = (
  a: TvShowSeason | TvShowEpisode,
  b: TvShowSeason | TvShowEpisode
) => Number(a?.sortOrder) - Number(b?.sortOrder)

const compareDisplayNumber = (
  a: TvShowSeason | TvShowEpisode,
  b: TvShowSeason | TvShowEpisode
) => Number(a?.displayNumber) - Number(b?.displayNumber)

// GET /v3/tvshows/{tvShowId}/season/{seasonId}/episodes
// GET /v3/tvshows/{tvShowId}/episodes
export const $tvShowSeasonEpisodes = createStore<TvShowEpisode[]>([])
  .on(getTvShowEpisodesBySeasonByPageFx.doneData, (_, page) =>
    [...page.content].sort(compareSortOrder)
  )
  .on(getTvShowEpisodesWithoutSeasonByPageFx.doneData, (_, page) =>
    [...page.content].sort(compareSortOrder)
  )
  .on(
    [postMarkEpisodeWatchedFx, postMarkEpisodeWatchedWithoutSeasonFx],
    updateEpisodesWatchedHandler(updateEpisodeWatchedHandler)
  )
  .on(
    [
      postMarkEpisodeWatchedFx.done.map(({ params }) => params),
      postMarkEpisodeWatchedWithoutSeasonFx.done.map(({ params }) => params),
    ],
    updateEpisodesWatchedHandler(updateEpisodeWatchedSuccessHandler)
  )
  .on(
    [
      postMarkEpisodeWatchedFx.fail.map(({ params }) => params),
      postMarkEpisodeWatchedWithoutSeasonFx.fail.map(({ params }) => params),
    ],
    updateEpisodesWatchedHandler(updateEpisodeWatchedFailHandler)
  )

export const $tvShowEpisodesPageInfo = createStore<PageInfo | null>(null)
  .on(
    getTvShowEpisodesBySeasonByPageFx.doneData,
    (_, { content: _ignored, ...pageInfo }) => pageInfo
  )
  .on(
    getTvShowEpisodesWithoutSeasonByPageFx.doneData,
    (_, { content: _ignored, ...pageInfo }) => pageInfo
  )

// GET /v3/tvshows/{tvShowId}/seasons
export const $tvShowSeasons = createStore<TvShowSeason[]>([])
  .on(getTvShowSeasonsByPageFx.doneData, (_, page) =>
    [...page.content].sort(compareDisplayNumber)
  )
  .reset(getTvShowSeasonsByPageFx)

export const $tvShowSeasonsPageInfo = createStore<PageInfo | null>(null)
  .on(
    getTvShowSeasonsByPageFx.doneData,
    (_, { content: _ignored, ...pageInfo }) => pageInfo
  )
  .reset(getTvShowSeasonsByPageFx)

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}
export const $tvShowSeason = createStore<TvShowSeason | null>(null).on(
  getTvShowSeasonByIdFx.doneData,
  (_, season) => season
)

// GET /v3/tvshows/{tvShowId}/seasons/{seasonId}/episodes/{episodeId}/url
// GET /v3/tvshows/{tvShowId}/episodes/{episodeId}/url
export const $tvShowEpisodeUrl = createStore<MediaUrl | null>(null)
  .on(getTvShowEpisodeUrlFx.doneData, (_, urlObject) => urlObject)
  .on(getEpisodeUrlWithoutSeasonFx.doneData, (_, urlObject) => urlObject)

getTvShowEpisodesSmartFx.use(async ({ tvShowId, page, ...rest }) => {
  if ('seasonId' in rest && typeof rest?.seasonId === 'number')
    return await getTvShowEpisodesBySeasonByPageBaseFx({
      tvShowId,
      seasonId: Number(rest.seasonId),
      page,
      ...rest,
    })

  return await getTvShowEpisodesWithoutSeasonByPageBaseFx({
    tvShowId,
    page,
    ...rest,
  })
})

/**************** $recommendedTvShows *******************/

export const $recommendedTvShows = createStore<TvShowRecommended[]>([]).on(
  getRecommendedTvShowsFx.doneData,
  (_, doneData) => doneData
)
/**************** $recommendedTvShows *******************/

/******************* initDetailsTvShows ********************/

export const $currentTvShow = createStore<CurrentTvShow | null>(null)
  .on(getTvShowFx.doneData, (_, shows) => shows?.[0] || null)
  .on(updateTvShowFx, (state, params) => {
    return params.id === state?.id //
      ? updateSingleHandler(state, params)
      : state
  })
  .on(updateTvShowFx.done, (state, { params }) => {
    return params.id === state?.id
      ? updateSingleSuccessHandler(state, params)
      : state
  })
  .on(updateTvShowFx.fail, (state, { params }) => {
    return params.id === state?.id
      ? updateSingleFailHandler(state, params)
      : state
  })

/******************* initDetailsTvShows ********************/

/******************* $tvShowEpisodesBySeason ********************/
// store that aggregates preloaded/previously loaded episodes to allow fast routing between seasons/episodes on client
// use getTvShowEpisodesBySeasonByPageFx to paginate over episodes
const showPlayListReceived = createEvent<TVShowsPlaylist>()
const showPlayListFailed = createEvent<TVShowsPlaylist>()

const $latestShowPlayListTvShowId = createStore<number | null>(null).on(
  getSeasonsWithEpisodesFx,
  (_, { tvShowId }) => tvShowId
)

sample({
  clock: getSeasonsWithEpisodesFx.done,
  source: $latestShowPlayListTvShowId,
  filter: (latestTvShowId, { params }) => latestTvShowId === params.tvShowId,
  fn: (_, { result }) => result,
  target: showPlayListReceived,
})

sample({
  clock: getSeasonsWithEpisodesFx.fail,
  source: $latestShowPlayListTvShowId,
  filter: (latestTvShowId, { params }) => latestTvShowId === params.tvShowId,
  fn: (_, { error }) => ({
    error:
      error != null &&
      typeof error === 'object' &&
      'message' in error &&
      typeof error.message === 'string'
        ? error.message
        : undefined,
  }),
  target: showPlayListFailed,
})

export const $showPlayList = createStore<TVShowsPlaylist>({})
  .on(showPlayListReceived, (_, payload) => payload)
  .on(showPlayListFailed, (_, payload) => payload)

/******************* $tvShowEpisodesBySeason ********************/

/***************** $tvShowCategoriesWithShows *******************/

export const initCategoriesWithShowsFx = attach({
  effect: getCategoriesWithShowsFx,
  mapParams: (params) => ({ ...params, page: 0 }),
})

export const loadCategoriesWithShowsFx = attach({
  effect: getCategoriesWithShowsFx, // use to paginate over subCategories
})

// to paginate over categories use - loadCategoriesWithChannelsFx
export const $tvShowCategoriesWithShows = createStore<CategoryWithShows[]>([])
  .on(initCategoriesWithShowsFx.doneData, (_, payload) => {
    return payload.content.map((page) => ({
      ...page,
      content: [...page.content.content],
    }))
  })
  .on(loadCategoriesWithShowsFx.doneData, (state, payload) => {
    const newContent = payload.content.map((page) => ({
      ...page,
      content: [...page.content.content],
    }))
    return [...state, ...newContent]
  })

// pagination of main array of categories $tvCategoriesWithChannels
export const $pageInfoCategoriesWithShows = createStore<PageableInfo | null>(
  null
).on(
  [initCategoriesWithShowsFx.done, loadCategoriesWithShowsFx.done],
  (_, { result: { content: _ignored, ...pageInfo }, params }) => ({
    ...pageInfo,
    params,
  })
)

/***************** $tvShowCategoriesWithShows *******************/

/***************** $seriesPlaylist *******************/

interface SeriesPlaylistNoSettings extends Omit<
  SeriesPlaylist,
  'isLooped' | 'isAutoplay'
> {}

export const generateSeriesPlaylistFx: ApiEffect<
  IGenerateSeriesPlaylistParams,
  SeriesPlaylistNoSettings
> = createEffect()

export const continueGenSeasonLessSeriesPlaylistFx: ApiEffect<
  {
    tvShowId: number
    episodeId?: number | null
    isLooped?: boolean
    isAutoplay?: boolean
    categoryId?: number | null
    showIndex?: number | null
  },
  SeriesPlaylistNoSettings
> = createEffect()

export const continueGenWithSeasonSeriesPlaylistFx: ApiEffect<
  {
    tvShowId: number
    seasonId: number
    episodeId?: number | null
    isLooped?: boolean
    isAutoplay?: boolean
    categoryId?: number | null
    showIndex?: number | null
  },
  SeriesPlaylistNoSettings
> = createEffect()

export const recalculateCurrentSeriesPlaylist =
  createEvent<SeriesPlaylistRecalculate>() // call it after autoplay happened

const seriesPlaylistReceived = createEvent<SeriesPlaylistNoSettings>()

const $latestSeriesPlaylistParams =
  createStore<IGenerateSeriesPlaylistParams | null>(null).on(
    generateSeriesPlaylistFx,
    (_, params) => params
  )

const isSameSeriesPlaylistRequest = (
  latestParams: IGenerateSeriesPlaylistParams | null,
  params: IGenerateSeriesPlaylistParams
) =>
  latestParams?.tvShowId === params.tvShowId &&
  (latestParams?.seasonId ?? null) === (params.seasonId ?? null) &&
  (latestParams?.episodeId ?? null) === (params.episodeId ?? null)

sample({
  clock: generateSeriesPlaylistFx.done,
  source: $latestSeriesPlaylistParams,
  filter: (latestParams, { params }) =>
    isSameSeriesPlaylistRequest(latestParams, params),
  fn: (_, { result }) => result,
  target: seriesPlaylistReceived,
})

export const $seriesPlaylist = createStore<SeriesPlaylist | null>(null).on(
  seriesPlaylistReceived,
  (state, payload) =>
    produce(state, (draft) => ({
      ...payload,
      isLooped: Boolean(draft?.isLooped),
      isAutoplay: Boolean(draft?.isAutoplay),
    }))
)

sample({
  clock: recalculateCurrentSeriesPlaylist,
  fn: (data) => ({
    tvShowId: data.tvShowId,
    seasonId: data.seasonId,
    episodeId: data.episodeId,
    categoryId: data.categoryId,
    showIndex: data.showIndex,

    isLooped: Boolean(data.isLooped),
    isAutoplay: Boolean(data.isAutoplay),
  }),
  target: generateSeriesPlaylistFx,
})

/***************** $seriesPlaylist *******************/

/**************** $showsByCategory *******************/

export const loadShowsFx = attach({ effect: getShowsByCategoryIdFx }) // for pagination over shows

export const initShowsCategoryFx: ApiEffect<
  {
    categoryId: number
    count?: number
  },
  CategoryWithShows | null
> = createEffect()

export const $showsByCategory = createStore<CategoryWithShows | null>(null)
  .on(initShowsCategoryFx.doneData, (_ignored, payload) => payload)
  .on(loadShowsFx.doneData, (state, { content }) => {
    return produce(state, (draft) => {
      if (draft) {
        draft.content = [...draft.content, ...content]
      }
    })
  })

export const $showsByCategoryPageInfo = createStore<PageableInfo | null>(
  null
).on(
  loadShowsFx.done,
  (
    _,
    {
      result: { content: _ignored, ...pageInfo },
      params: { signal: _ignored1, ...params },
    }
  ) => ({
    ...pageInfo,
    params,
  })
)
/**************** $showsByCategory *******************/

/**************** $subCategoriesWithShows *******************/

export const loadSubCategoriesWithShowsFx: ApiEffect<
  { categoryId: number; page: number; count?: number },
  void
> = createEffect() // use to paginate over subCategories

export const initShowsSubCategoriesFx = attach({
  effect: loadSubCategoriesWithShowsFx,
  mapParams: (params) => ({ ...params, page: 0 }),
})
export const loadParentCategoryFx = attach({
  effect: getTvShowCategoryFx,
})
export const loadSubCategoriesShowsFx = attach({
  effect: getTvShowSubcategoriesByCategoryFx,
})
export const loadShowsOfSubcategoryFx = attach({
  effect: getShowsByCategoryIdFx,
}) // to paginate over sub arrays use - loadShowsOfSubcategoryFx

export const $parentCategoryShows = createStore<TvShowCategory | null>(null).on(
  loadParentCategoryFx.doneData,
  (_ignored, payload) => payload
)

export const $subCategoriesWithShows = createStore<CategoryWithShows[]>([])
  .on(loadSubCategoriesShowsFx.doneData, (state, payload) => {
    const newCategories = payload.content.map((category) => ({
      ...category,
      content: [],
      isMoreThanOnePageSubCategories:
        Number(category.subCategories?.length) > DEFAULT_PAGE_SIZE,
    }))
    return [...state, ...newCategories]
  })
  .on(loadShowsOfSubcategoryFx.done, (_, payload) => {
    const { content: newShows } = payload.result
    const { categoryId } = payload.params

    return produce(_, (draft) => {
      const arrayIndex = draft.findIndex(({ id }) => id === categoryId)

      if (!draft[arrayIndex]) return
      draft[arrayIndex].content.push(...newShows)
      draft[arrayIndex].isMoreThanOnePage =
        !payload.result.last ||
        draft[arrayIndex].content.length > DEFAULT_PAGE_SIZE
    })
  })

// pagination of main array of categories $subCategoriesWithShows
export const $pageInfoSubCategoriesWithShows = createStore<PageableInfo | null>(
  null
).on(
  loadSubCategoriesShowsFx.done,
  (
    _,
    {
      result: { content: _ignored, ...pageInfo },
      params: { signal: _ignored1, ...params },
    }
  ) => ({
    ...pageInfo,
    params,
  })
)

/**************** $subCategoriesWithShows *******************/

/**************** $infiniteShowsCategory *******************/

export const initInfiniteShowsCategoryFx = attach({
  effect: getAllTvShowsFx,
  mapParams: (params) => ({ ...params, page: 0 }),
})

const fillFakeCategory = (
  shows: readonly TvShow[],
  isMoreThanOnePage: boolean
): CategoryWithShows => ({
  id: 0,
  name: 'All',
  sortOrder: 0,
  parentCategoryId: 0,
  isMoreThanOnePage,
  isMoreThanOnePageSubCategories: false,
  content: [...shows],
  tvShowQuantity: 0,
  subCategories: [],
})

// to paginate over categories use - loadInfiniteShowsCategoryFx
export const $infiniteShowsCategory = createStore<CategoryWithShows | null>(
  null
).on(initInfiniteShowsCategoryFx.doneData, (_, payload) =>
  fillFakeCategory(payload.content, !payload.last)
)

/**************** $infiniteShowsCategory *******************/
